/**
 * @description Shared Elavon merchant-alias editor for Super Admin surfaces.
 *
 * Visible only when Elavon is assigned + enabled for the target company.
 * Writes companies.merchant_alias via PATCH /api/admin/companies/:id.
 *
 * @dependencies
 * - adminApi.updateAdminCompany
 *
 * @notes
 * - One SoT column. Do not write gateway credentials.
 * - Unique violation surfaces as a 409 banner.
 */

import { useEffect, useState } from 'react';
import { AlertCircle, Loader2 } from 'lucide-react';
import { updateAdminCompany } from '../../services/api/adminApi';

/**
 * @param {object} props
 * @param {string} props.companyId
 * @param {boolean} props.elavonAssigned
 * @param {string|null} [props.initialAlias]
 * @param {function} [props.onSaved]
 */
function MerchantAliasEditor({ companyId, elavonAssigned, initialAlias = '', onSaved }) {
  const [alias, setAlias] = useState(initialAlias || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setAlias(initialAlias || '');
    setError(null);
    setSaved(false);
  }, [companyId, initialAlias]);

  if (!elavonAssigned) {
    return null;
  }

  const handleSave = async () => {
    if (!companyId || saving) return;
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const result = await updateAdminCompany(companyId, { merchantAlias: alias.trim() });
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to save merchant alias');
      }
      setSaved(true);
      onSaved?.(result.data);
    } catch (err) {
      setError(
        err.response?.data?.error?.message ||
          err.message ||
          'Failed to save merchant alias'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <p className="text-sm font-medium text-gray-900">Merchant Alias</p>
      <p className="text-xs text-gray-500 mt-0.5">
        Emailed by Elavon after boarding. Required for card charges. Elavon decides if it is live.
      </p>
      <div className="mt-3 flex items-center gap-2">
        <input
          type="text"
          value={alias}
          onChange={(e) => {
            setAlias(e.target.value);
            setSaved(false);
          }}
          className="flex-1 min-w-0 rounded-lg border border-gray-300 px-3 py-2 text-sm"
          placeholder="Merchant alias"
          autoComplete="off"
        />
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-500 disabled:opacity-50"
        >
          {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
          Save
        </button>
      </div>
      {saved && <p className="mt-2 text-xs text-green-700">Alias saved.</p>}
      {error && (
        <div className="mt-2 p-2 rounded-md bg-red-50 border border-red-200 text-xs text-red-700 flex items-start gap-2">
          <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
}

export default MerchantAliasEditor;
